/**
 * L'état d'une leçon pour l'élève — et, quand elle est fermée, **pourquoi**.
 *
 * « Verrouillé » sans motif produit un écran où l'élève ne sait pas quoi faire.
 * Les deux motifs du serveur appellent deux conduites opposées :
 * `exercise_not_passed` dit de retourner travailler, `awaiting_grading` dit
 * d'attendre. Les fondre en un seul message en rendrait un des deux faux.
 */
import { useTranslation } from "react-i18next";

import type { LessonProgress } from "@/features/learning/api/useLearning";
import { cn } from "@/shared/lib/cn";

const TONES: Record<LessonProgress["state"], string> = {
  locked: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  available: "bg-brand-50 text-brand-700 dark:bg-slate-800 dark:text-brand-300",
  in_progress: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  completed: "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200",
};

export function LessonStateBadge({ lesson }: { lesson: LessonProgress }) {
  const { t } = useTranslation("learning");
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        TONES[lesson.state],
      )}
    >
      {t(`state.${lesson.state}`)}
    </span>
  );
}

/**
 * Le motif du verrou, en toutes lettres.
 *
 * Rend `null` sur une leçon ouverte plutôt qu'une chaîne vide : un élément vide
 * occuperait une place dans la mise en page et laisserait croire à une
 * information manquante.
 */
export function LockReason({ lesson }: { lesson: LessonProgress }) {
  const { t } = useTranslation("learning");
  if (lesson.state !== "locked" || lesson.lock_reason === null) {
    return null;
  }
  return (
    <p className="text-xs text-slate-500 dark:text-slate-400">
      {t(`lockReason.${lesson.lock_reason}`)}
    </p>
  );
}
